import type { AdminEventRegistration } from "../services/eventRegistrationAdminTypes";
import {
  formatPaymentStatusLabel,
  formatRegistrationShirtSizes,
  formatRegistrationStatusLabel,
} from "../../events/shared/utils/eventRegistrationFormatters";

type RegistrationExportRow = {
  event: string;
  category: string;
  registrationType: string;
  player: string;
  playerCpf: string;
  partner: string;
  partnerCpf: string;
  email: string;
  status: string;
  payment: string;
  paymentMethod: string;
  amount: string;
  restriction: string;
  shirts: string;
};

function csvEscape(value: unknown) {
  const text = String(value ?? "");
  if (!/[;"\n\r]/.test(text)) return text;
  return `"${text.replace(/"/g, '""')}"`;
}

function htmlEscape(value: unknown) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function buildFileSlug(value: string) {
  return (
    value
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 60) || "inscritos"
  );
}

function formatCurrencyFromCents(value?: number | null) {
  const cents = Number(value || 0);
  if (!Number.isFinite(cents) || cents <= 0) return "R$ 0,00";
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(cents / 100);
}

function registrationValue(row: AdminEventRegistration) {
  return (
    row.finalAmountCents ??
    row.paymentAmountCents ??
    row.originalAmountCents ??
    0
  );
}

function eventName(row: AdminEventRegistration) {
  return (
    String(row.challengeName || "").trim() ||
    String(row.challengeType || "").trim() ||
    `Evento ${row.eventId}`
  );
}

function registrationCategory(row: AdminEventRegistration) {
  return (
    String(row.registrationCategoryName || "").trim() ||
    String(row.registrationCategoryKey || "").trim() ||
    "Categoria não informada"
  );
}

function paymentMethodLabel(method?: string | null) {
  const normalized = String(method || "")
    .trim()
    .toLowerCase();
  if (normalized === "pix") return "PIX";
  if (normalized === "credit_card") return "Cartão";
  if (normalized === "debit_card") return "Débito";
  if (normalized === "cash" || normalized === "money") return "Dinheiro";
  return "Não informado";
}

function restrictionLabel(row: AdminEventRegistration) {
  const details = row.availabilityRestriction?.details || [];
  const labels = details
    .map((item) => String(item.label || "").trim())
    .filter(Boolean);
  const fallback = String(row.availabilityRestriction?.label || "").trim();
  if (labels.length > 0) return labels.join(" | ");
  return fallback || "Sem restrição";
}

export function buildRegistrationExportRows(
  registrations: AdminEventRegistration[],
): RegistrationExportRow[] {
  return registrations.map((row) => ({
    event: eventName(row),
    category: registrationCategory(row),
    registrationType:
      row.registrationType === "fixed_pair" ? "Dupla fixa" : "Individual",
    player: String(row.userName || "Atleta TNP").trim(),
    playerCpf: String(row.userCpf || "").trim(),
    partner: String(row.partnerName || "").trim(),
    partnerCpf: String(row.partnerCpf || "").trim(),
    email: String(row.userEmail || "").trim(),
    status: formatRegistrationStatusLabel(row.status),
    payment: row.paymentRequired
      ? formatPaymentStatusLabel(row.paymentStatus)
      : "Não requerido",
    paymentMethod: row.paymentRequired
      ? paymentMethodLabel(row.paymentMethod)
      : "Não requerido",
    amount: row.paymentRequired
      ? formatCurrencyFromCents(registrationValue(row))
      : "Não requerido",
    restriction: restrictionLabel(row),
    shirts: formatRegistrationShirtSizes(row) || "",
  }));
}

export function buildRegistrationListCsv(
  registrations: AdminEventRegistration[],
) {
  const rows = buildRegistrationExportRows(registrations);
  const header = [
    "Evento",
    "Categoria",
    "Inscricao",
    "Atleta",
    "CPF atleta",
    "Parceiro",
    "CPF parceiro",
    "E-mail",
    "Status",
    "Pagamento",
    "Metodo",
    "Valor",
    "Restricao",
    "Camisetas",
  ];
  const lines = [
    header.join(";"),
    ...rows.map((row) =>
      [
        row.event,
        row.category,
        row.registrationType,
        row.player,
        row.playerCpf,
        row.partner,
        row.partnerCpf,
        row.email,
        row.status,
        row.payment,
        row.paymentMethod,
        row.amount,
        row.restriction,
        row.shirts,
      ]
        .map(csvEscape)
        .join(";"),
    ),
  ];
  return `﻿${lines.join("\n")}`;
}

export function downloadRegistrationListCsv({
  registrations,
  title = "lista-de-inscritos",
}: {
  registrations: AdminEventRegistration[];
  title?: string;
}) {
  if (registrations.length === 0) return false;
  const blob = new Blob([buildRegistrationListCsv(registrations)], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `${buildFileSlug(title)}.csv`;
  anchor.click();
  URL.revokeObjectURL(url);
  return true;
}

export function buildRegistrationPrintHtml({
  generatedAt = new Date(),
  registrations,
  title = "Lista de inscritos",
}: {
  generatedAt?: Date;
  registrations: AdminEventRegistration[];
  title?: string;
}) {
  const rows = buildRegistrationExportRows(registrations);
  const generatedLabel = new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(generatedAt);
  const tableRows = rows
    .map(
      (row, index) => `
        <tr>
          <td>${index + 1}</td>
          <td>${htmlEscape(row.player)}</td>
          <td>${htmlEscape(row.partner || "-")}</td>
          <td>${htmlEscape(row.category)}</td>
          <td>${htmlEscape(row.status)}</td>
          <td>${htmlEscape(row.payment)}</td>
          <td>${htmlEscape(row.amount)}</td>
          <td>${htmlEscape(row.restriction)}</td>
        </tr>`,
    )
    .join("");

  return `<!doctype html>
<html lang="pt-BR">
  <head>
    <meta charset="utf-8" />
    <title>${htmlEscape(title)}</title>
    <style>
      body { color: #111827; font-family: Arial, sans-serif; margin: 28px; }
      h1 { font-size: 24px; margin: 0 0 6px; }
      p { color: #4b5563; margin: 0 0 18px; }
      table { border-collapse: collapse; width: 100%; }
      th, td { border: 1px solid #d1d5db; font-size: 12px; padding: 8px; text-align: left; vertical-align: top; }
      th { background: #f3f4f6; color: #111827; }
      @media print { body { margin: 12mm; } }
    </style>
  </head>
  <body>
    <h1>${htmlEscape(title)}</h1>
    <p>${rows.length} inscrição(ões) · Gerado em ${htmlEscape(generatedLabel)}</p>
    <table>
      <thead>
        <tr>
          <th>#</th>
          <th>Atleta</th>
          <th>Parceiro</th>
          <th>Categoria</th>
          <th>Status</th>
          <th>Pagamento</th>
          <th>Valor</th>
          <th>Restrição</th>
        </tr>
      </thead>
      <tbody>${tableRows}</tbody>
    </table>
  </body>
</html>`;
}

export function printRegistrationList({
  registrations,
  title = "Lista de inscritos",
}: {
  registrations: AdminEventRegistration[];
  title?: string;
}) {
  if (registrations.length === 0) return false;
  const printWindow = window.open("", "_blank", "width=1024,height=768");
  if (!printWindow) return false;
  printWindow.document.open();
  printWindow.document.write(
    buildRegistrationPrintHtml({ registrations, title }),
  );
  printWindow.document.close();
  printWindow.focus();
  printWindow.print();
  return true;
}
